import { useEffect, useState } from "react";
import { toast } from "@fox1ck-org/ui/toast";
import { Button, Input, NativeSelect, PageState, SettingRow, SettingsSection, TimezoneSelect } from "@fox1ck-org/ui";
import { useSaas } from "@fox1ck-org/auth/saas/react";
import { ExternalLink } from "lucide-react";

import { CatalogPicker } from "@/components/canonical/pickers/catalog-picker";
import { useT } from "@/lib/i18n";
import { RoleGate } from "@/components/auth/role-gate";
import { useTrackerRoles } from "@/lib/permissions";
import { workspaceFormSchema } from "@/lib/schemas/workspace";
import { useListPresets } from "@/lib/services/goal-service";
import { useGetWorkspace, useUpdateWorkspace, type WorkspaceSettingsForm } from "@/lib/services/workspace-service";
import { AppearanceSettings } from "@/shell/appearance";

/**
 * Настройки › Общие (доска Settings): the workspace — its name, the zone its
 * days are cut in, the currency of the reports, the domain and the goal set
 * new campaigns start from — then Оформление. Every change is saved at once
 * («изменения сохраняются сразу»): a field on leaving it, a choice on picking.
 */
export default function WorkspaceSettingsPage() {
  const t = useT();
  const workspace = useGetWorkspace();
  const presets = useListPresets();
  const update = useUpdateWorkspace();
  // UpdateWorkspace is RequireManage: a buyer reads the settings, an admin changes them.
  const canManage = useTrackerRoles().canManage;
  const { links } = useSaas();
  const ws = workspace.data;
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState<string | undefined>();
  useEffect(() => setName(ws?.name ?? ""), [ws?.name]);

  if (workspace.isPending) {
    return <PageState mode="loading" title={t("ws.loading")} />;
  }
  if (workspace.isError || !ws) {
    return <PageState mode="error" title={t("ws.saveFailed")} error={workspace.error} retry={() => void workspace.refetch()} retryLabel={t("lib.common.retry")} />;
  }

  const current: WorkspaceSettingsForm = {
    name: ws.name,
    defaultTrackingDomainId: ws.defaultTrackingDomainId,
    defaultGoalPreset: ws.defaultGoalPreset,
    timezone: ws.timezone,
  };
  const save = (patch: Partial<WorkspaceSettingsForm>) => {
    const next = { ...current, ...patch };
    const parsed = workspaceFormSchema.safeParse({ name: next.name, domainId: next.defaultTrackingDomainId, goalPreset: next.defaultGoalPreset, timezone: next.timezone ?? "" });
    if (!parsed.success) {
      setNameError(parsed.error.issues[0]?.message);
      return;
    }
    setNameError(undefined);
    update.mutate(next, {
      onSuccess: () => toast.success(t("ws.saved")),
      onError: (e) => toast.error(e instanceof Error ? e.message : t("ws.saveFailed")),
    });
  };

  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-6 pt-1">
      <div className="flex max-w-275 flex-col gap-6">
        <RoleGate need="manage" variant="panel" title={t("ops.set.workspace")} />
        <SettingsSection title={t("ops.set.workspace")}>
          <SettingRow label={t("ws.name")} description={t("ops.set.name.hint")} controlId="ws-name" error={nameError}>
            <Input
              id="ws-name"
              font="sans"
              value={name}
              maxLength={120}
              disabled={!canManage}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => name.trim() !== ws.name && save({ name })}
              className="max-w-90"
            />
          </SettingRow>
          <SettingRow label={t("ws.tz")} description={t("ops.set.tz.hint")} controlId="ws-tz">
            <TimezoneSelect id="ws-tz" value={ws.timezone || "Europe/Kyiv"} onChange={(tz) => tz !== ws.timezone && save({ timezone: tz })} disabled={!canManage} className="max-w-90" />
          </SettingRow>
          <SettingRow label={t("ops.set.currency")} description={t("ops.set.currency.hint")} controlId="ws-currency">
            {/* The reports are kept in USD; there is no other currency to pick. */}
            <NativeSelect id="ws-currency" value="USD" disabled className="max-w-90">
              <option value="USD">{t("ops.set.currency.usd")}</option>
            </NativeSelect>
          </SettingRow>
          <SettingRow label={t("ws.domain")} description={t("ws.domain.hint")}>
            <div className="max-w-90">
              <CatalogPicker kind="domain" clearable value={ws.defaultTrackingDomainId} onChange={(id) => save({ defaultTrackingDomainId: id || "" })} disabled={!canManage} />
            </div>
          </SettingRow>
          <SettingRow label={t("ws.preset")} description={t("ws.preset.hint")} controlId="ws-preset">
            <NativeSelect id="ws-preset" value={ws.defaultGoalPreset} onChange={(e) => save({ defaultGoalPreset: e.target.value })} disabled={!canManage} className="max-w-90">
              <option value="">{t("ws.preset.none")}</option>
              {(presets.data ?? []).map((p) => (
                <option key={p.key} value={p.key}>
                  {p.displayName || p.key}
                </option>
              ))}
            </NativeSelect>
          </SettingRow>
        </SettingsSection>
        {/* Roles are console's: «Участники» sets each member's tracker role (owner or org admin). */}
        <SettingsSection title={t("ws.people")}>
          <SettingRow label={t("ws.people")} description={t("ws.people.hint")} controlId="ws-people">
            <Button asChild variant="secondary" id="ws-people">
              <a href={links.members()} target="_blank" rel="noreferrer noopener">
                {t("ws.people.open")} <ExternalLink className="size-4" aria-hidden />
              </a>
            </Button>
          </SettingRow>
        </SettingsSection>
        <AppearanceSettings />
      </div>
    </div>
  );
}
